import { useId } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Modal, Button, Input } from '@/components/ui'
import { useCancelShiftAssignment } from '../hooks/useShifts'
import {
  cancelShiftAssignmentSchema,
  type CancelShiftAssignmentFormData,
} from '../schemas/shift.schemas'

export interface CancelAssignmentModalProps {
  companyId: string
  assignmentId: string | null
  onClose: () => void
  onSuccess?: () => void
}

export function CancelAssignmentModal({
  companyId,
  assignmentId,
  onClose,
  onSuccess,
}: CancelAssignmentModalProps): React.ReactElement {
  const formId = useId()
  const cancelAssignment = useCancelShiftAssignment()
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CancelShiftAssignmentFormData>({
    resolver: zodResolver(cancelShiftAssignmentSchema),
    defaultValues: { cancellationReason: '' },
  })

  const onSubmit = (data: CancelShiftAssignmentFormData) => {
    if (!assignmentId) return
    cancelAssignment.mutate(
      {
        companyId,
        id: assignmentId,
        payload: { cancellationReason: data.cancellationReason },
      },
      {
        onSuccess: () => {
          reset()
          onClose()
          onSuccess?.()
        },
      }
    )
  }

  if (!assignmentId) return <></>

  return (
    <Modal
      open={!!assignmentId}
      onClose={onClose}
      title="Cancel assignment"
      className="max-w-md"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            Back
          </Button>
          <Button
            form={formId}
            type="submit"
            variant="danger"
            loading={cancelAssignment.isPending}
          >
            Cancel assignment
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <Input
          label="Cancellation reason"
          {...register('cancellationReason')}
          error={errors.cancellationReason?.message}
          placeholder="e.g. Staff unavailable"
          required
        />
      </form>
    </Modal>
  )
}

CancelAssignmentModal.displayName = 'CancelAssignmentModal'
